// Tracks how many `Modal`s are currently open, app-wide, so global
// window-level keyboard-shortcut listeners (useEntrySelection's m/Delete/
// Enter/arrows, the Feeds page's own shortcuts) can bail out while a modal
// has the user's attention — otherwise a keydown meant for a focused button
// inside the modal also reaches those listeners, since they're bound
// directly on `window` and modals don't stop propagation. Generic on
// purpose, same shape as `activeRegion.tsx`.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface ModalStackValue {
  count: number;
  push: () => void;
  pop: () => void;
}

const ModalStackContext = createContext<ModalStackValue | null>(null);

export function ModalStackProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);
  const push = useCallback(() => setCount((c) => c + 1), []);
  const pop = useCallback(() => setCount((c) => Math.max(0, c - 1)), []);
  const value = useMemo(() => ({ count, push, pop }), [count, push, pop]);
  return <ModalStackContext.Provider value={value}>{children}</ModalStackContext.Provider>;
}

export function useAnyModalOpen(): boolean {
  const ctx = useContext(ModalStackContext);
  if (!ctx) throw new Error("useAnyModalOpen must be used within a ModalStackProvider");
  return ctx.count > 0;
}

/** Registers `open` into the shared modal-open count for as long as it's true. */
export function useRegisterModalOpen(open: boolean) {
  const ctx = useContext(ModalStackContext);
  if (!ctx) throw new Error("useRegisterModalOpen must be used within a ModalStackProvider");
  const { push, pop } = ctx;
  useEffect(() => {
    if (!open) return;
    push();
    return pop;
  }, [open, push, pop]);
}
